import { createClient } from "@supabase/supabase-js";
import type {
  AuthChangeEvent,
  Session,
  SupabaseClient,
} from "@supabase/supabase-js";

export type BrowserAuthMode = "local" | "supabase";

export class BrowserAuthConfigurationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BrowserAuthConfigurationError";
  }
}

let client: SupabaseClient | null = null;

export function browserAuthMode(): BrowserAuthMode {
  const configured =
    import.meta.env.VITE_PIPELINE_172X_AUTH_MODE?.trim().toLowerCase() ||
    "local";
  if (configured !== "local" && configured !== "supabase") {
    throw new BrowserAuthConfigurationError(
      "VITE_PIPELINE_172X_AUTH_MODE must be local or supabase.",
    );
  }
  return configured;
}

export function getSupabaseClient(): SupabaseClient {
  if (browserAuthMode() !== "supabase") {
    throw new BrowserAuthConfigurationError(
      "Supabase Auth is not selected for this browser build.",
    );
  }
  if (client) return client;

  const url = import.meta.env.VITE_SUPABASE_URL?.trim();
  const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim();
  if (!url || !publishableKey) {
    throw new BrowserAuthConfigurationError(
      "Supabase browser configuration is incomplete.",
    );
  }
  validateSupabaseUrl(url);
  client = createClient(url, publishableKey, {
    auth: {
      flowType: "pkce",
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
    },
  });
  return client;
}

export function authCallbackUrl(): string {
  return new URL("/auth/callback", window.location.origin).toString();
}

export async function currentSupabaseAccessToken(): Promise<string | null> {
  if (browserAuthMode() !== "supabase") return null;
  const {
    data: { session },
    error,
  } = await getSupabaseClient().auth.getSession();
  if (error) {
    throw new BrowserAuthConfigurationError(
      "The Supabase browser session is unavailable.",
    );
  }
  return session?.access_token ?? null;
}

export function subscribeToSupabaseAuth(
  listener: (event: AuthChangeEvent, session: Session | null) => void,
): () => void {
  if (browserAuthMode() !== "supabase") return () => {};
  const {
    data: { subscription },
  } = getSupabaseClient().auth.onAuthStateChange(listener);
  return () => subscription.unsubscribe();
}

function validateSupabaseUrl(value: string): void {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new BrowserAuthConfigurationError(
      "VITE_SUPABASE_URL must be a valid URL.",
    );
  }
  const localHttp =
    url.protocol === "http:" &&
    (url.hostname === "127.0.0.1" || url.hostname === "localhost");
  if (url.protocol !== "https:" && !localHttp) {
    throw new BrowserAuthConfigurationError(
      "VITE_SUPABASE_URL must use HTTPS or local loopback HTTP.",
    );
  }
}
